import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Heart, ChevronRight, Check, User, Sparkles, Palette } from 'lucide-react';
import { useProgress } from '../context/ProgressContext';
interface FormData {
  age: number | null;
  gender: 'male' | 'female' | 'non-binary' | 'prefer-not-to-say' | null;
  pronouns: string;
  theme: 'light' | 'dark';
  name: string;
}
export function OnboardingPage() {
  const navigate = useNavigate();
  const {
    completeOnboarding
  } = useProgress();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState<FormData>({
    age: null,
    gender: null,
    pronouns: 'they/them',
    theme: 'light',
    name: ''
  });
  const handleComplete = () => {
    if (formData.age && formData.gender) {
      completeOnboarding({
        age: formData.age,
        name: '',
        pronouns: formData.pronouns,
        gender: formData.gender,
        language: 'en',
        theme: formData.theme,
        matureTopics: formData.age >= 16,
        hasCompletedOnboarding: true
      });
      navigate('/home');
    }
  };
  const totalSteps = 4;
  const handleGenderSelect = (gender: 'male' | 'female' | 'non-binary' | 'prefer-not-to-say') => {
    let pronouns = 'they/them';
    if (gender === 'male') pronouns = 'he/him';
    if (gender === 'female') pronouns = 'she/her';
    setFormData({
      ...formData,
      gender,
      pronouns
    });
  };
  const shouldShowPronounStep = formData.gender === 'non-binary' || formData.gender === 'prefer-not-to-say';
  const StepIndicator = ({
    stepNumber,
    label,
    isComplete
  }: {
    stepNumber: number;
    label: string;
    isComplete: boolean;
  }) => <div className="flex items-center space-x-3" data-id="element-617">
      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-all ${isComplete ? 'bg-green-500 text-white' : stepNumber === step ? 'bg-blue-500 text-white' : 'bg-gray-200 dark:bg-slate-700 text-gray-400'}`} data-id="element-618">
        {isComplete ? <Check className="w-5 h-5" data-id="element-619" /> : stepNumber}
      </div>
      <div className="hidden md:block" data-id="element-620">
        <p className={`text-sm font-semibold ${isComplete || stepNumber === step ? 'text-gray-900 dark:text-white' : 'text-gray-400'}`} data-id="element-621">
          {label}
        </p>
      </div>
    </div>;
  return <div className="min-h-screen w-full bg-gradient-to-br from-blue-900 via-purple-900 to-pink-900 flex items-center justify-center p-4" data-id="element-622">
      <div className="max-w-2xl w-full bg-white dark:bg-slate-800 rounded-3xl shadow-2xl p-8" data-id="element-623">
        {/* Progress Bar */}
        <div className="h-3 bg-gray-100 dark:bg-slate-700 relative overflow-hidden rounded-full mb-6" data-id="element-624">
          <div className="h-full bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 transition-all duration-500 ease-out" style={{
          width: `${step / totalSteps * 100}%`
        }} data-id="element-625" />
        </div>
        {/* Step Indicators */}
        <div className="bg-gray-50 dark:bg-slate-900 px-6 py-4 border-b border-gray-200 dark:border-slate-700 rounded-t-xl mb-6" data-id="element-626">
          <div className="flex items-center justify-between max-w-3xl mx-auto" data-id="element-627">
            <StepIndicator stepNumber={1} label="Welcome" isComplete={step > 1} data-id="element-628" />
            <div className="flex-1 h-0.5 bg-gray-200 dark:bg-slate-700 mx-2" data-id="element-629" />
            <StepIndicator stepNumber={2} label="Age" isComplete={step > 2} data-id="element-630" />
            <div className="flex-1 h-0.5 bg-gray-200 dark:bg-slate-700 mx-2" data-id="element-631" />
            <StepIndicator stepNumber={3} label="Identity" isComplete={step > 3} data-id="element-632" />
            <div className="flex-1 h-0.5 bg-gray-200 dark:bg-slate-700 mx-2" data-id="element-633" />
            <StepIndicator stepNumber={4} label="Preferences" isComplete={step > 4} data-id="element-634" />
          </div>
        </div>
        <div className="p-8 md:p-12" data-id="element-635">
          {/* Step 1: Welcome */}
          {step === 1 && <div className="text-center animate-fadeIn" data-id="element-636">
              <div className="w-24 h-24 bg-gradient-to-br from-blue-500 to-purple-500 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-xl animate-bounce" data-id="element-637">
                <Heart className="w-12 h-12 text-white" data-id="element-638" />
              </div>
              <h1 className="text-5xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-4" data-id="element-639">
                Hey there, friend! 👋
              </h1>
              <p className="text-2xl text-gray-700 dark:text-gray-300 mb-6 font-medium" data-id="element-640">
                Welcome to ItMatters
              </p>
              <p className="text-lg text-gray-600 dark:text-gray-400 mb-8 leading-relaxed max-w-2xl mx-auto" data-id="element-641">
                This is your safe space to learn about growing up,
                relationships, and health. No judgment, no shame—just honest
                answers to your questions. We're here to help you feel confident
                and informed!
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8 max-w-3xl mx-auto" data-id="element-642">
                <div className="bg-blue-50 dark:bg-slate-700 rounded-2xl p-6 text-center" data-id="element-643">
                  <div className="text-4xl mb-3" data-id="element-644">🔒</div>
                  <h3 className="font-bold text-gray-900 dark:text-white mb-2" data-id="element-645">
                    100% Private
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300" data-id="element-646">
                    Everything is anonymous. We don't track who you are.
                  </p>
                </div>
                <div className="bg-purple-50 dark:bg-slate-700 rounded-2xl p-6 text-center" data-id="element-647">
                  <div className="text-4xl mb-3" data-id="element-648">🎯</div>
                  <h3 className="font-bold text-gray-900 dark:text-white mb-2" data-id="element-649">
                    Just For You
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300" data-id="element-650">
                    Content tailored to your age and needs.
                  </p>
                </div>
                <div className="bg-pink-50 dark:bg-slate-700 rounded-2xl p-6 text-center" data-id="element-651">
                  <div className="text-4xl mb-3" data-id="element-652">💪</div>
                  <h3 className="font-bold text-gray-900 dark:text-white mb-2" data-id="element-653">
                    Learn & Grow
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300" data-id="element-654">
                    Build confidence through stories, quizzes, and advice.
                  </p>
                </div>
              </div>
              <button onClick={() => setStep(2)} className="w-full max-w-md mx-auto py-4 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 text-white text-lg font-bold rounded-2xl hover:shadow-2xl transition-all flex items-center justify-center space-x-3 group" data-id="element-655">
                <span data-id="element-656">Let's Get Started!</span>
                <ChevronRight className="w-6 h-6 group-hover:translate-x-2 transition-transform" data-id="element-657" />
              </button>
            </div>}
          {/* Step 2: Age */}
          {step === 2 && <div className="animate-fadeIn" data-id="element-658">
              <div className="text-center mb-8" data-id="element-659">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center mx-auto mb-4" data-id="element-660">
                  <span className="text-3xl" data-id="element-661">🎂</span>
                </div>
                <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-3" data-id="element-662">
                  How old are you?
                </h2>
                <p className="text-gray-600 dark:text-gray-300 text-lg max-w-2xl mx-auto" data-id="element-663">
                  This helps us show you content that's perfect for your age.
                  Everything is filtered to be appropriate and helpful for where
                  you are in life!
                </p>
              </div>
              <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mb-8 max-w-3xl mx-auto" data-id="element-664">
                {[13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24].map(age => <button key={age} onClick={() => setFormData({
              ...formData,
              age
            })} className={`py-6 rounded-2xl border-3 font-bold text-xl transition-all transform hover:scale-105 ${formData.age === age ? 'bg-gradient-to-br from-blue-500 to-purple-500 text-white border-blue-500 shadow-xl scale-105' : 'bg-white dark:bg-slate-700 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-slate-600 hover:border-blue-300 hover:shadow-lg'}`} data-id="element-665">
                    {age}
                  </button>)}
              </div>
              {formData.age && <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-slate-700 dark:to-slate-600 border-l-4 border-blue-500 p-6 rounded-xl mb-6 animate-fadeIn" data-id="element-666">
                  <div className="flex items-start space-x-3" data-id="element-667">
                    <Sparkles className="w-6 h-6 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-1" data-id="element-668" />
                    <div data-id="element-669">
                      <p className="font-bold text-blue-900 dark:text-blue-300 mb-2 text-lg" data-id="element-670">
                        Perfect! Here's what you'll get:
                      </p>
                      <ul className="text-blue-800 dark:text-blue-200 space-y-1 text-sm" data-id="element-671">
                        {formData.age >= 13 && formData.age <= 15 && <>
                            <li data-id="element-672">✓ Body basics, puberty, and hygiene</li>
                            <li data-id="element-673">✓ Understanding emotions and self-esteem</li>
                            <li data-id="element-674">✓ Friendship skills and peer pressure</li>
                            <li data-id="element-675">✓ Online safety and digital wellness</li>
                          </>}
                        {formData.age >= 16 && formData.age <= 17 && <>
                            <li data-id="element-676">✓ Everything from ages 13-15, PLUS:</li>
                            <li data-id="element-677">✓ Dating, relationships, and consent</li>
                            <li data-id="element-678">✓ Mental health and stress management</li>
                            <li data-id="element-679">✓ Identity exploration and self-discovery</li>
                          </>}
                        {formData.age >= 18 && <>
                            <li data-id="element-680">✓ Complete access to all content</li>
                            <li data-id="element-681">✓ Advanced relationship topics</li>
                            <li data-id="element-682">✓ Sexual health and contraception</li>
                            <li data-id="element-683">✓ Adult life skills and independence</li>
                          </>}
                      </ul>
                    </div>
                  </div>
                </div>}
              <div className="flex space-x-3 max-w-3xl mx-auto" data-id="element-684">
                <button onClick={() => setStep(1)} className="flex-1 py-4 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-200 font-semibold rounded-xl hover:bg-gray-200 dark:hover:bg-slate-600 transition-all" data-id="element-685">
                  Back
                </button>
                <button onClick={() => setStep(3)} disabled={!formData.age} className="flex-1 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-bold rounded-xl hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2" data-id="element-686">
                  <span data-id="element-687">Continue</span>
                  <ChevronRight className="w-5 h-5" data-id="element-688" />
                </button>
              </div>
            </div>}
          {/* Step 3: Gender Identity */}
          {step === 3 && <div className="animate-fadeIn" data-id="element-689">
              <div className="text-center mb-8" data-id="element-690">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center mx-auto mb-4" data-id="element-691">
                  <User className="w-8 h-8 text-white" data-id="element-692" />
                </div>
                <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-3" data-id="element-693">
                  How do you identify?
                </h2>
                <p className="text-gray-600 dark:text-gray-300 text-lg max-w-2xl mx-auto" data-id="element-694">
                  This helps us use the right language and show you content that
                  feels right for you. You can skip this or change it anytime!
                </p>
              </div>
              <div className="space-y-4 mb-8 max-w-2xl mx-auto" data-id="element-695">
                {[{
              value: 'male',
              label: 'Male',
              emoji: '👦',
              description: 'Content tailored for male experiences'
            }, {
              value: 'female',
              label: 'Female',
              emoji: '👧',
              description: 'Content tailored for female experiences'
            }, {
              value: 'non-binary',
              label: 'Non-binary',
              emoji: '🌈',
              description: 'Inclusive, gender-neutral content'
            }, {
              value: 'prefer-not-to-say',
              label: 'Prefer not to say',
              emoji: '✨',
              description: 'General content for everyone'
            }].map(option => <button key={option.value} onClick={() => handleGenderSelect(option.value as any)} className={`w-full p-6 rounded-2xl border-3 transition-all text-left transform hover:scale-102 ${formData.gender === option.value ? 'bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/30 dark:to-pink-900/30 border-purple-500 shadow-lg scale-102' : 'bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-600 hover:border-purple-300 hover:shadow-md'}`} data-id="element-696">
                    <div className="flex items-center space-x-4" data-id="element-697">
                      <div className="text-4xl" data-id="element-698">{option.emoji}</div>
                      <div className="flex-1" data-id="element-699">
                        <p className="font-bold text-gray-900 dark:text-white text-lg mb-1" data-id="element-700">
                          {option.label}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-300" data-id="element-701">
                          {option.description}
                        </p>
                      </div>
                      {formData.gender === option.value && <Check className="w-6 h-6 text-purple-600 dark:text-purple-400" data-id="element-702" />}
                    </div>
                  </button>)}
              </div>
              <div className="flex space-x-3 max-w-2xl mx-auto" data-id="element-703">
                <button onClick={() => setStep(2)} className="flex-1 py-4 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-200 font-semibold rounded-xl hover:bg-gray-200 dark:hover:bg-slate-600 transition-all" data-id="element-704">
                  Back
                </button>
                <button onClick={() => setStep(4)} className="flex-1 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold rounded-xl hover:shadow-lg transition-all flex items-center justify-center space-x-2" data-id="element-705">
                  <span data-id="element-706">Continue</span>
                  <ChevronRight className="w-5 h-5" data-id="element-707" />
                </button>
              </div>
            </div>}
          {/* Step 4: Preferences */}
          {step === 4 && <div className="animate-fadeIn" data-id="element-708">
              <div className="text-center mb-8" data-id="element-709">
                <div className="w-16 h-16 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-2xl flex items-center justify-center mx-auto mb-4" data-id="element-710">
                  <Palette className="w-8 h-8 text-white" data-id="element-711" />
                </div>
                <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-3" data-id="element-712">
                  Almost there! 🎉
                </h2>
                <p className="text-gray-600 dark:text-gray-300 text-lg max-w-2xl mx-auto" data-id="element-713">
                  Just a couple more preferences to make your experience
                  perfect. You can change these anytime in settings!
                </p>
              </div>
              <div className="space-y-6 mb-8 max-w-2xl mx-auto" data-id="element-714">
                <div className="bg-white dark:bg-slate-700 border-3 border-gray-200 dark:border-slate-600 rounded-2xl p-8" data-id="element-715">
                  <div className="flex items-start justify-between mb-4" data-id="element-716">
                    <div data-id="element-717">
                      <p className="font-bold text-gray-900 dark:text-white text-xl mb-2" data-id="element-718">
                        Theme Preference
                      </p>
                      <p className="text-gray-600 dark:text-gray-300" data-id="element-719">
                        Choose the look that's easiest on your eyes
                      </p>
                    </div>
                  </div>
                  <div className="flex space-x-4 mt-6" data-id="element-720">
                    <button onClick={() => setFormData({
                  ...formData,
                  theme: 'light'
                })} className={`flex-1 py-6 rounded-xl border-3 font-semibold transition-all transform hover:scale-105 ${formData.theme === 'light' ? 'bg-gradient-to-br from-yellow-400 to-orange-400 text-white border-yellow-500 shadow-xl' : 'bg-gray-50 dark:bg-slate-800 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-slate-600'}`} data-id="element-721">
                      <div className="text-3xl mb-2" data-id="element-722">☀️</div>
                      <div data-id="element-723">Light Mode</div>
                    </button>
                    <button onClick={() => setFormData({
                  ...formData,
                  theme: 'dark'
                })} className={`flex-1 py-6 rounded-xl border-3 font-semibold transition-all transform hover:scale-105 ${formData.theme === 'dark' ? 'bg-gradient-to-br from-indigo-600 to-purple-600 text-white border-indigo-500 shadow-xl' : 'bg-gray-50 dark:bg-slate-800 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-slate-600'}`} data-id="element-724">
                      <div className="text-3xl mb-2" data-id="element-725">🌙</div>
                      <div data-id="element-726">Dark Mode</div>
                    </button>
                  </div>
                </div>
                {formData.age && formData.age >= 18 && <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 border-l-4 border-green-500 p-6 rounded-xl animate-fadeIn" data-id="element-727">
                    <div className="flex items-start space-x-3" data-id="element-728">
                      <div className="text-3xl" data-id="element-729">✓</div>
                      <div data-id="element-730">
                        <p className="font-bold text-green-900 dark:text-green-300 mb-2 text-lg" data-id="element-731">
                          Full Access Unlocked!
                        </p>
                        <p className="text-green-800 dark:text-green-200" data-id="element-732">
                          As you're 18 or older, you'll have access to
                          comprehensive content about relationships, health, and
                          well-being—all within legal and cultural guidelines.
                        </p>
                      </div>
                    </div>
                  </div>}
                {formData.age && formData.age < 18 && <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/30 dark:to-purple-900/30 border-l-4 border-blue-500 p-6 rounded-xl animate-fadeIn" data-id="element-733">
                    <div className="flex items-start space-x-3" data-id="element-734">
                      <Shield className="w-8 h-8 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-1" data-id="element-735" />
                      <div data-id="element-736">
                        <p className="font-bold text-blue-900 dark:text-blue-300 mb-2 text-lg" data-id="element-737">
                          Age-Appropriate Content
                        </p>
                        <p className="text-blue-800 dark:text-blue-200" data-id="element-738">
                          Content is carefully filtered to be suitable for your
                          age group, focusing on health basics, emotional
                          wellness, and safe relationships. We've got your back!
                        </p>
                      </div>
                    </div>
                  </div>}
              </div>
              <div className="flex space-x-3 max-w-2xl mx-auto" data-id="element-739">
                <button onClick={() => setStep(3)} className="flex-1 py-4 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-200 font-semibold rounded-xl hover:bg-gray-200 dark:hover:bg-slate-600 transition-all" data-id="element-740">
                  Back
                </button>
                <button onClick={handleComplete} className="flex-1 py-4 bg-gradient-to-r from-green-600 via-emerald-600 to-teal-600 text-white font-bold rounded-xl hover:shadow-2xl transition-all text-lg flex items-center justify-center space-x-2 group" data-id="element-741">
                  <span data-id="element-742">Start Your Journey!</span>
                  <Sparkles className="w-5 h-5 group-hover:rotate-12 transition-transform" data-id="element-743" />
                </button>
              </div>
            </div>}
        </div>
      </div>
    </div>;
}